// src/lib/readinessGate.js — pure helpers for the readiness-gate UI flow.
//
// §"Readiness gate" (docs/training_methodology.md): a refused plan surfaces
// the server's three honest options; an override is explicit and logged,
// never silent. The React components (GoalWizard, App) call these helpers so
// the decisions are unit-tested in server/test/readinessGate.test.js.
//
// CommonJS + no object spread, same constraint as the generator mirror
// (CLAUDE.md §"Generator mirror") so CRA consumes it via module.exports.

/** Wizard distance key for a km value (inverse of GoalWizard's DIST table). */
function distKeyForKm(km) {
  const k = Number(km) || 0;
  if (Math.abs(k - 5) < 0.3) return '5K';
  if (Math.abs(k - 10) < 0.3) return '10K';
  if (Math.abs(k - 21.0975) < 0.6) return 'HM';
  if (Math.abs(k - 42.195) < 0.8) return 'M';
  if (Math.abs(k - 50) < 0.5) return '50K';
  if (Math.abs(k - 100) < 0.5) return '100K';
  if (Math.abs(k - 161) < 1.5) return '100M';
  return 'custom';
}

/**
 * Which options the gate screen offers for a blocked result.
 * - readiness: explicit override, later date (server suggestion), shorter
 *   distance (server suggestion), finish-only (only with a non-zero baseline:
 *   a finish-only plan ramps forward FROM the baseline, so at 0 km/wk it
 *   would be empty — see report "Decisions needed").
 * - pace-guard: adjust the target time only (the guard has no override and
 *   also applies to finish-only plans).
 */
function gateOptionsFor(gate) {
  const out = { acknowledge: false, adjustTime: false, fixManualPace: false, laterDate: null, shorterDistance: null, finishOnly: false };
  if (!gate || !gate.blocked) return out;
  if (gate.reason === 'pace-guard') {
    // A refused MANUAL threshold pace is fixed in Settings, not by changing
    // the goal time — offer the right fix for the cause.
    if (gate.paceGuard && gate.paceGuard.source === 'manual') out.fixManualPace = true;
    else out.adjustTime = true;
    return out;
  }
  if (gate.reason !== 'readiness') return out;
  const r = gate.readiness || {};
  const o = r.options || {};
  out.acknowledge = true;
  out.laterDate = o.laterDate && o.laterDate.suggestedRaceDate ? o.laterDate.suggestedRaceDate : null;
  out.shorterDistance = o.shorterDistance && Number(o.shorterDistance.km) > 0
    ? { label: o.shorterDistance.label, km: Number(o.shorterDistance.km), key: distKeyForKm(o.shorterDistance.km) }
    : null;
  out.finishOnly = !!(o.finishOnly && o.finishOnly.available !== false) && Number(r.baselineWeeklyKm) > 0;
  return out;
}

/** Apply a gate action's overrides to a wizard payload (returns a copy). */
function applyGateOverrides(payload, opts) {
  const pl = Object.assign({}, payload || {});
  const o = opts || {};
  if (o.overrideRaceDate) { pl.raceDate = o.overrideRaceDate; pl.someday = false; }
  if (o.overrideDistanceKm) pl.distanceKm = Number(o.overrideDistanceKm);
  if (o.overrideNoTarget) { pl.mode = 'finish'; pl.time = ''; }
  return pl;
}

/** True when a milestone already exists as a live B goal (retry dedupe). */
function isDuplicateMilestone(existingGoals, m) {
  return (existingGoals || []).some(function (g) {
    return g && g.priority === 'B' && g.status !== 'dropped'
      && Math.abs(Number(g.distanceKm) - Number(m.distanceKm)) < 0.5
      && (g.raceDate || '') === (m.date || '');
  });
}

/**
 * acknowledgeReadiness for a REGENERATE of an existing plan (day re-layout,
 * format-version migration): carry forward only an override the athlete made
 * explicitly on that plan. A goal-driven rebuild always passes false.
 */
function regenerateAcknowledge(plan) {
  return !!(plan && plan.acknowledgedStretch === true);
}

/**
 * Whole weeks the GENERATOR sees between now and the race (Monday-start
 * weeks, rounded) — so the banner asks the same question the gate answers.
 */
function generatorWeeksAvailable(raceDateIso, now) {
  const mondayOf = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const dow = x.getDay();
    x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow));
    return x;
  };
  const parts = String(raceDateIso || '').split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const race = new Date(parts[0], parts[1] - 1, parts[2]);
  const weeks = Math.round((+mondayOf(race) - +mondayOf(now != null ? now : Date.now())) / (7 * 86400000));
  return Math.max(1, weeks);
}

/**
 * Readiness banner fields from the generator's own assessReadiness() verdict
 * (§"Readiness gate"). Replaces the client-side re-implementation that used a
 * mean of active weeks, a 1.65× threshold and no short-distance peak.
 * `gapFactor` is requiredStart / 4-week median — the same ratio the gate
 * refuses above 1.5. null when there is nothing to show.
 */
function bannerFromReadiness(v) {
  if (!v || v.ready || !v.hasBaseline) return null;
  const o = v.options || {};
  const base = Number(v.baselineWeeklyKm) || 0;
  return {
    ready: false,
    baselineWeeklyKm: base,
    requiredStart: v.requiredStart,
    peakKm: v.peakKm,
    gapFactor: base > 0 ? Math.round((v.requiredStart / base) * 10) / 10 : null,
    suggestedRaceDate: o.laterDate ? o.laterDate.suggestedRaceDate : null,
    suggestedWeeks: o.laterDate ? o.laterDate.suggestedWeeks : null,
    saferDistance: o.shorterDistance ? o.shorterDistance.label : null,
    saferDistanceKm: o.shorterDistance ? o.shorterDistance.km : null,
    finishOnly: base > 0,
  };
}

/**
 * True when accepted adjustments were applied to this plan (coachEngine
 * appends { at, applied: [...] } to changeLog). Such a plan is never
 * auto-regenerated on a format-version bump — a regenerate rebuilds from the
 * goals and would silently discard the athlete's accepted changes.
 */
function planHasAppliedChanges(plan) {
  return Array.isArray(plan && plan.changeLog)
    && plan.changeLog.some(function (e) { return e && Array.isArray(e.applied) && e.applied.length > 0; });
}

module.exports = {
  planHasAppliedChanges,
  generatorWeeksAvailable,
  bannerFromReadiness,
  distKeyForKm,
  gateOptionsFor,
  applyGateOverrides,
  isDuplicateMilestone,
  regenerateAcknowledge,
};
